const MAX_DESCRIPTION_LENGTH = 500;
const MAX_URL_LENGTH = 2048;
const MAX_HANDLE_RECORD_LENGTH = 200;

const X_HANDLE_RE = /^[A-Za-z0-9_]{1,15}$/;
const GITHUB_HANDLE_RE =
  /^[A-Za-z0-9](?:[A-Za-z0-9-]{0,37}[A-Za-z0-9])?$/;

function hasUnpairedSurrogate(value) {
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    if (code >= 0xd800 && code <= 0xdbff) {
      const next = value.charCodeAt(index + 1);
      if (!(next >= 0xdc00 && next <= 0xdfff)) return true;
      index += 1;
    } else if (code >= 0xdc00 && code <= 0xdfff) {
      return true;
    }
  }
  return false;
}

function normalizeUnicode(value) {
  if (typeof value !== "string" || hasUnpairedSurrogate(value)) return null;

  try {
    return value.normalize("NFC");
  } catch {
    return null;
  }
}

function stripUnsafeControls(value) {
  return Array.from(value)
    .filter((character) => {
      const code = character.codePointAt(0);
      return !(
        (code >= 0x00 && code <= 0x08) ||
        code === 0x0b ||
        code === 0x0c ||
        (code >= 0x0e && code <= 0x1f) ||
        (code >= 0x7f && code <= 0x9f) ||
        code === 0x061c ||
        code === 0x200e ||
        code === 0x200f ||
        (code >= 0x202a && code <= 0x202e) ||
        (code >= 0x2066 && code <= 0x2069)
      );
    })
    .join("");
}

function isEmptyValue(value) {
  return value == null || (typeof value === "string" && !value.trim());
}

export function sanitizeEnsDescription(value) {
  const normalized = normalizeUnicode(value);
  if (!normalized) return null;

  const sanitized = stripUnsafeControls(normalized)
    .replace(/<[^>]*>/g, "")
    .replace(/[<>]/g, "")
    .replace(/\s+/g, " ")
    .trim();

  if (!sanitized) return null;
  return Array.from(sanitized).slice(0, MAX_DESCRIPTION_LENGTH).join("");
}

export function sanitizeHttpsUrl(value) {
  const normalized = normalizeUnicode(value);
  if (!normalized || stripUnsafeControls(normalized) !== normalized) return null;

  const trimmed = normalized.trim();
  if (!trimmed || trimmed.length > MAX_URL_LENGTH) return null;

  try {
    const url = new URL(trimmed);
    if (
      url.protocol !== "https:" ||
      !url.hostname ||
      url.username ||
      url.password
    ) {
      return null;
    }
    const canonicalUrl = url.toString();
    return canonicalUrl.length <= MAX_URL_LENGTH ? canonicalUrl : null;
  } catch {
    return null;
  }
}

function getHandleCandidate(value, allowedHosts) {
  const normalized = normalizeUnicode(value);
  if (!normalized || stripUnsafeControls(normalized) !== normalized) return null;

  const trimmed = normalized.trim();
  if (!trimmed || trimmed.length > MAX_HANDLE_RECORD_LENGTH) return null;

  if (!trimmed.includes("://")) {
    return trimmed.startsWith("@") ? trimmed.slice(1) : trimmed;
  }

  const safeUrl = sanitizeHttpsUrl(trimmed);
  if (!safeUrl) return null;

  const url = new URL(safeUrl);
  const hostname = url.hostname.toLowerCase();
  if (!allowedHosts.includes(hostname) || url.search || url.hash) return null;

  const segments = url.pathname.split("/").filter(Boolean);
  return segments.length === 1 ? segments[0] : null;
}

export function sanitizeXProfile(value) {
  const handle = getHandleCandidate(value, [
    "x.com",
    "www.x.com",
    "twitter.com",
    "www.twitter.com",
  ]);
  if (!handle || !X_HANDLE_RE.test(handle)) return null;
  return `https://x.com/${handle}`;
}

export function sanitizeGithubHandle(value) {
  const handle = getHandleCandidate(value, [
    "github.com",
    "www.github.com",
  ]);
  if (!handle || !GITHUB_HANDLE_RE.test(handle)) return null;
  return handle;
}

export function sanitizeEnsProfile({
  avatar,
  description,
  url,
  x,
  twitter,
  github,
} = {}) {
  return {
    avatarUrl: sanitizeHttpsUrl(avatar),
    description: sanitizeEnsDescription(description),
    websiteUrl: sanitizeHttpsUrl(url),
    xUrl: sanitizeXProfile(x) || sanitizeXProfile(twitter),
    githubHandle: sanitizeGithubHandle(github),
  };
}

export function mergeEnsProfileIntoEmptyFields(
  formData,
  ensProfile,
  role
) {
  if (!formData || !ensProfile) return formData;

  let next = formData;
  const assignIfEmpty = (field, value) => {
    if (!value || !isEmptyValue(next[field])) return;
    if (next === formData) next = { ...formData };
    next[field] = value;
  };

  assignIfEmpty("profile_image", ensProfile.avatarUrl);
  assignIfEmpty("x", ensProfile.xUrl);

  if (role === "applicant") {
    assignIfEmpty("biography", ensProfile.description);
  } else if (role === "organization") {
    assignIfEmpty("description", ensProfile.description);
  }

  if (ensProfile.websiteUrl) {
    const websites = Array.isArray(next.websites) ? next.websites : [""];
    const emptyIndex = websites.findIndex(isEmptyValue);
    if (emptyIndex >= 0) {
      if (next === formData) next = { ...formData };
      next.websites = [...websites];
      next.websites[emptyIndex] = ensProfile.websiteUrl;
    }
  }

  return next;
}
